import { useState, useContext } from "react";
import { LangContext } from "../App";
import "bootstrap/dist/css/bootstrap.css";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";

// Wyciągnięte style na górę – spójne z komponentem Job
const styles = {
  wrapper: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    padding: "80px 24px",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  container: {
    maxWidth: "900px", // nieco węższy kontener dla lepszego czytania FAQ
    margin: "0 auto",
    width: "100%",
  },
  headerZone: {
    textAlign: "center",
    marginBottom: "50px",
  },
  mainTitle: {
    fontSize: "36px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.02em",
    margin: "0 0 16px 0",
  },
  faqList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
  },
  faqItem: {
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.04)",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  questionButton: {
    width: "100%",
    background: "linear-gradient(135deg, #1E3A5F 0%, #274C77 100%)",
    padding: "16px 24px",
    border: "none",
    textAlign: "center", // Wyśrodkowanie tekstu zgodnie z Twoją preferencją
    color: "#ffffff",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    transition: "opacity 0.2s ease-in-out",
  },
  questionText: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
  },
  answerContainer: {
    padding: "24px",
    color: "#4b5563",
    fontSize: "16px",
    lineHeight: "1.6",
    textAlign: "left",
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
};

const FAQ = () => {
  const language = useContext(LangContext);
  const [activeId, setActiveId] = useState(null);

  const toggleFAQ = (id) => {
    setActiveId(activeId === id ? null : id);
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const faqData = [
    {
      id: 0,
      questionPl: "Jak wygląda realizacja projektu od strony technicznej?",
      answerPl: `Realizację projektu dzielę na kilka etapów:

1. Analiza i planowanie – ustalamy wymagania, funkcjonalności aplikacji, zakres prac oraz harmonogram realizacji.

2. Infrastruktura i domena – dobieram i konfiguruję serwer VPS, rejestruję lub podłączam domenę, konfiguruję DNS, certyfikat SSL oraz środowisko serwerowe.

3. Bezpieczeństwo – wdrażam zabezpieczenia serwera, kontrolę dostępu, szyfrowanie połączeń HTTPS oraz dwuetapowy system uwierzytelniania (2FA), jeśli wymaga tego projekt.

4. Tworzenie aplikacji – projektuję bazę danych, API oraz interfejs użytkownika. Implementuję uzgodnione funkcjonalności, integracje z zewnętrznymi usługami i mechanizmy automatyzacji.

5. Metodologia Agile – pracuję etapami (Sprintami). Regularnie prezentuję postępy, konsultuję rozwiązania i uwzględniam uwagi, dzięki czemu masz bieżący wpływ na kierunek rozwoju projektu.

6. Testowanie i wdrożenie – sprawdzam poprawność działania aplikacji, bezpieczeństwo, responsywność i wydajność. Następnie wdrażam gotowy produkt na serwer produkcyjny.

7. Utrzymanie i rozwój – konfiguruję kopie zapasowe, monitoruję działanie środowiska, aktualizuję oprogramowanie i zapewniam wsparcie techniczne oraz możliwość dalszego rozwoju aplikacji.`,

      questionEn: "How does the technical implementation of a project work?",

      answerEn: `I divide the project implementation process into several stages:

1. Analysis and planning – we define the requirements, application features, project scope, and implementation schedule.

2. Infrastructure and domain – I select and configure a VPS server, register or connect the domain, configure DNS, install an SSL certificate, and prepare the server environment.

3. Security – I implement server security measures, access control, HTTPS encryption, and two-factor authentication (2FA), if required by the project.

4. Application development – I design the database, API, and user interface. I implement the agreed features, integrations with external services, and automation mechanisms.

5. Agile methodology – I work in stages (Sprints). I regularly present progress, discuss solutions, and incorporate feedback, giving you ongoing influence over the direction of the project.

6. Testing and deployment – I verify the application's functionality, security, responsiveness, and performance. Once everything is ready, I deploy the finished product to the production server.

7. Maintenance and further development – I configure backups, monitor the environment, update software, and provide technical support and opportunities for further application development.`,
    },
    {
      id: 1,
      questionPl: "Jak wygląda współpraca?",
      answerPl:
        "Rozpoczynam od rozmowy o Twoich potrzebach i celach biznesowych. Następnie przygotowuję koncepcję rozwiązania, wycenę oraz harmonogram. Po akceptacji przechodzę do realizacji projektu i jego wdrożenia.",
      questionEn: "What does the cooperation look like?",
      answerEn:
        "I start with a discussion about your needs and business goals. Then I prepare a solution concept, quote, and schedule. Upon approval, I move on to project development and deployment.",
    },
    {
      id: 2,
      questionPl: "Jakie aplikacje tworzysz?",
      answerPl:
        "Projektuję nowoczesne aplikacje webowe, systemy CRM, portale internetowe, panele administracyjne, aplikacje SPA oraz dedykowane rozwiązania wspierające procesy biznesowe.",
      questionEn: "What kind of applications do you create?",
      answerEn:
        "I design modern web applications, CRM systems, web portals, admin panels, SPA applications, and dedicated solutions supporting business processes.",
    },
    {
      id: 3,
      questionPl: "Jakie technologie wykorzystujesz?",
      answerPl:
        "Pracuję głównie z React.js, JavaScript, PHP, MySQL, Bootstrap oraz REST API. Dobieram technologie do potrzeb projektu, dbając o wydajność, bezpieczeństwo i możliwość dalszego rozwoju.",
      questionEn: "What technologies do you use?",
      answerEn:
        "I mainly work with React.js, JavaScript, PHP, MySQL, Bootstrap, and REST APIs. I select technologies tailored to the project's needs, focusing on performance, security, and scalability.",
    },
    {
      id: 4,
      questionPl: "Czy modernizujesz istniejące aplikacje?",
      answerPl:
        "Tak. Mogę rozbudować, zoptymalizować lub unowocześnić istniejące systemy, poprawić ich wydajność, bezpieczeństwo oraz dostosować je do nowych potrzeb biznesowych.",
      questionEn: "Do you modernize existing applications?",
      answerEn:
        "Yes. I can expand, optimize, or modernize existing systems, improve their performance and security, and adapt them to new business needs.",
    },
    {
      id: 5,
      questionPl: "Czy wykonujesz projekty od podstaw?",
      answerPl:
        "Tak. Realizuję projekty od analizy wymagań, przez projektowanie i implementację, aż po wdrożenie produkcyjne i dalszy rozwój aplikacji.",
      questionEn: "Do you build projects from scratch?",
      answerEn:
        "Yes. I handle projects from requirements analysis, through design and implementation, up to production deployment and further application development.",
    },
    {
      id: 6,
      questionPl: "Ile kosztuje stworzenie aplikacji?",
      answerPl:
        "Koszt zależy od zakresu funkcjonalności i stopnia złożoności projektu. Po krótkiej rozmowie przygotowuję bezpłatną, indywidualną wycenę.",
      questionEn: "How much does it cost to build an application?",
      answerEn:
        "The cost depends on the scope of features and the complexity of the project. After a short conversation, I prepare a free, individual quote.",
    },
    {
      id: 7,
      questionPl: "Jak długo trwa realizacja projektu?",
      answerPl:
        "Czas realizacji zależy od wielkości projektu. Mniejsze aplikacje powstają zwykle w ciągu kilku tygodni, bardziej rozbudowane systemy wymagają kilku miesięcy pracy.",
      questionEn: "How long does project implementation take?",
      answerEn:
        "The duration depends on the size of the project. Smaller applications are usually built within a few weeks, while more complex systems require several months of work.",
    },
    {
      id: 8,
      questionPl: "Czy zapewniasz wsparcie po wdrożeniu?",
      answerPl:
        "Tak. Oferuję utrzymanie aplikacji, aktualizacje, rozwój funkcjonalności oraz bieżące wsparcie techniczne.",
      questionEn: "Do you provide post-deployment support?",
      answerEn:
        "Yes. I offer application maintenance, updates, feature development, and ongoing technical support.",
    },
    {
      id: 9,
      questionPl: "Czy podpisujesz umowę i NDA?",
      answerPl:
        "Tak. W razie potrzeby podpisuję umowę o współpracy oraz umowę o zachowaniu poufności (NDA).",
      questionEn: "Do you sign contracts and NDAs?",
      answerEn:
        "Yes. If necessary, I sign a cooperation agreement and a Non-Disclosure Agreement (NDA).",
    },
    {
      id: 10,
      questionPl: "Czy pomagasz dobrać najlepsze rozwiązanie?",
      answerPl:
        "Tak. Jeśli nie masz gotowej specyfikacji, pomogę przeanalizować potrzeby i zaproponuję rozwiązanie najlepiej odpowiadające celom biznesowym oraz budżetowi.",
      questionEn: "Do you help choose the best solution?",
      answerEn:
        "Yes. If you don't have a ready-made specification, I will help analyze your needs and propose a solution that best fits your business goals and budget.",
    },
    {
      id: 11,
      questionPl: "Dlaczego warto współpracować właśnie ze mną?",
      answerPl:
        "Łączę doświadczenie programisty z wieloletnią praktyką w zarządzaniu działem IT. Dzięki temu patrzę na projekt zarówno od strony technologicznej, jak i biznesowej, proponując rozwiązania, które realnie wspierają rozwój firmy.",
      questionEn: "Why should you work with me?",
      answerEn:
        "I combine hands-on software development expertise with many years of experience managing IT departments. This allows me to approach every project from both a technical and a business perspective, delivering solutions that support long-term business growth.",
    },
    {
      id: 12,
      questionPl: "Czy współpracujesz z małymi firmami i startupami?",
      answerPl:
        "Tak. Realizuję projekty zarówno dla małych i średnich przedsiębiorstw, jak i większych organizacji. Zakres współpracy dostosowuję do potrzeb i budżetu klienta.",
      questionEn: "Do you work with small businesses and startups?",
      answerEn:
        "Yes. I work with startups, small and medium-sized businesses, as well as larger organizations. Every project is tailored to the client's specific needs, goals, and budget.",
    },
  ];

  const schemaFAQ = faqData.map((item) => ({
    "@type": "Question",
    name: language === "pl" ? item.questionPl : item.questionEn,
    acceptedAnswer: {
      "@type": "Answer",
      text: language === "pl" ? item.answerPl : item.answerEn,
    },
  }));

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: schemaFAQ,
          })}
        </script>
        <title>
          {language === "pl"
            ? "Często zadawane pytania | FAQ"
            : "Frequently Asked Questions | FAQ"}
        </title>
        <meta
          name="description"
          content={
            language === "pl"
              ? "Odpowiedzi na najczęściej zadawane pytania dotyczące tworzenia aplikacji, technologii i współpracy."
              : "Answers to frequently asked questions about application development, technologies, and cooperation."
          }
        />
      </Helmet>

      <div className="bodyBackground" style={styles.wrapper}>
        <AnimatePresence>
          <motion.div
            style={styles.container}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={textVariants}
          >
            <div style={styles.headerZone}>
              <h1 style={styles.mainTitle}>
                {language === "pl"
                  ? "Często zadawane pytania"
                  : "Frequently Asked Questions"}
              </h1>
            </div>

            <div style={styles.faqList}>
              {faqData.map((item) => {
                const isOpen = activeId === item.id;
                const question =
                  language === "pl" ? item.questionPl : item.questionEn;
                const answer =
                  language === "pl" ? item.answerPl : item.answerEn;

                return (
                  <div key={item.id} style={styles.faqItem}>
                    <button
                      style={{
                        ...styles.questionButton,
                        opacity: isOpen ? 0.95 : 1,
                      }}
                      onClick={() => toggleFAQ(item.id)}
                    >
                      <h2 style={styles.questionText}>{question}</h2>
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: "inline-block", fontSize: "14px" }}
                      >
                        ▼
                      </motion.span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          <div
                            style={{
                              ...styles.answerContainer,
                              whiteSpace: "pre-line",
                              lineHeight: 1.8,
                            }}
                          >
                            {answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  );
};

export default FAQ;
